import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { 
  Mail, 
  Lock, 
  Loader2, 
  AlertCircle, 
  ArrowLeft, 
  Phone,
  Shield,
  KeyRound,
  Chrome,
  Apple
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState(4);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutExpires, setLockoutExpires] = useState(null);
  
  // 2FA State
  const [requires2FA, setRequires2FA] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [demoOtp, setDemoOtp] = useState('');
  
  // OAuth config
  const [oauthConfig, setOauthConfig] = useState(null);

  useEffect(() => {
    // Fetch OAuth configuration
    const fetchOAuthConfig = async () => {
      try {
        const response = await axios.get(`${API}/auth/oauth-config`);
        setOauthConfig(response.data);
      } catch (err) {
        console.log('OAuth not configured');
      }
    };
    fetchOAuthConfig();
  }, []);

  useEffect(() => {
    // Handle lockout countdown
    if (lockoutExpires) {
      const interval = setInterval(() => {
        const now = new Date();
        const expiry = new Date(lockoutExpires);
        if (now >= expiry) {
          setIsLocked(false);
          setLockoutExpires(null);
          setAttemptsRemaining(4);
          clearInterval(interval);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [lockoutExpires]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      
      if (response.data.success) {
        if (response.data.requires_2fa) {
          // Show 2FA input
          setRequires2FA(true);
          setDemoOtp(response.data.demo_code || '');
          toast.info('Verification code sent to your phone');
        } else {
          // Direct login
          localStorage.setItem('companeze_user', JSON.stringify(response.data.user));
          localStorage.setItem('companeze_token', response.data.token);
          if (onLogin) onLogin();
          toast.success(`Welcome back, ${response.data.user.name}!`);
          navigate('/dashboard');
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object') {
        setError(detail.message || 'Invalid credentials');
        setAttemptsRemaining(detail.attempts_remaining ?? attemptsRemaining);
        if (detail.locked) {
          setIsLocked(true);
          setLockoutExpires(detail.lockout_expires);
        }
      } else {
        setError(detail || 'Invalid username or password');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/verify-2fa`, {
        username,
        code: otpCode
      });
      
      if (response.data.success) {
        localStorage.setItem('companeze_user', JSON.stringify(response.data.user));
        localStorage.setItem('companeze_token', response.data.token);
        if (onLogin) onLogin();
        toast.success(`Welcome back, ${response.data.user.name}!`);
        navigate('/dashboard');
      }
    } catch (err) {
      const detail = err.response?.data;
      setError(detail?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthLogin = async (provider) => {
    toast.info(`${provider} login coming soon! Please use username/password for now.`);
    // In production, this would redirect to OAuth provider
  };

  const handleDemoLogin = (demoUser, demoPass) => {
    setUsername(demoUser);
    setPassword(demoPass);
    setRequires2FA(false);
    setOtpCode('');
  };

  const getLockoutTimeRemaining = () => {
    if (!lockoutExpires) return '';
    const now = new Date();
    const expiry = new Date(lockoutExpires);
    const diff = Math.max(0, Math.floor((expiry - now) / 1000));
    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-900 transition-colors"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-900 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">Cz</span>
              </div>
              <span className="font-bold text-gray-900">Companeze</span>
              <span className="text-blue-900 text-sm font-semibold">BLDR</span>
            </div>
            <a 
              href="tel:5104224451" 
              className="flex items-center gap-2 text-gray-600 hover:text-blue-900 transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span>(510) 422-4451</span>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md bg-white shadow-xl border border-gray-200 overflow-hidden">
          {/* Navy Header */}
          <div className="bg-blue-900 px-8 py-8 text-center">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4">
              {requires2FA ? (
                <Shield className="w-8 h-8 text-blue-900" />
              ) : (
                <span className="text-2xl font-bold text-blue-900">Cz</span>
              )}
            </div>
            <h1 className="text-xl font-bold text-white mb-1">
              {requires2FA ? 'Two-Factor Authentication' : 'Client Login'}
            </h1>
            <p className="text-blue-200 text-sm">
              {requires2FA ? 'Enter the verification code sent to your phone' : 'Sign in to your Companeze account'}
            </p>
          </div>

          <CardContent className="p-8">
            {/* Lockout Warning */}
            {isLocked && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-red-700 font-semibold mb-2">
                  <AlertCircle className="w-5 h-5" />
                  Account Temporarily Locked
                </div>
                <p className="text-sm text-red-600">
                  Too many failed attempts. Please wait {getLockoutTimeRemaining()} before trying again.
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && !isLocked && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
                {attemptsRemaining > 0 && attemptsRemaining < 4 && (
                  <p className="text-xs text-red-600 mt-1">
                    {attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining
                  </p>
                )}
              </div>
            )}

            {/* 2FA Form */}
            {requires2FA ? (
              <form onSubmit={handleVerify2FA} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="otp" className="text-gray-700">Verification Code</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="otp"
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      className="h-12 pl-12 bg-gray-50 border-gray-200 focus:border-blue-900 text-center text-xl tracking-widest"
                      maxLength={6}
                      required
                      data-testid="otp-input"
                    />
                  </div>
                </div>

                {/* Demo OTP Display */}
                {demoOtp && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
                    <p className="text-xs text-amber-600 mb-1">Demo Mode - Use this code:</p>
                    <p className="text-2xl font-mono font-bold text-amber-700 tracking-widest">{demoOtp}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-white font-medium"
                  disabled={loading || otpCode.length !== 6}
                  data-testid="verify-2fa-btn"
                >
                  {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Verifying...</> : 'Verify & Sign In'}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setRequires2FA(false);
                    setOtpCode('');
                    setDemoOtp('');
                  }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </form>
            ) : (
              <>
                {/* Login Form */}
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="username" className="text-gray-700">Username</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="username"
                        type="text"
                        placeholder="Enter username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="h-12 pl-12 bg-gray-50 border-gray-200 focus:border-blue-900"
                        required
                        disabled={isLocked}
                        data-testid="username-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-gray-700">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="Enter password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-12 pl-12 bg-gray-50 border-gray-200 focus:border-blue-900"
                        required
                        disabled={isLocked}
                        data-testid="password-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-white font-medium"
                    disabled={loading || isLocked}
                    data-testid="login-btn"
                  >
                    {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Signing in...</> : 'Sign In'}
                  </Button>
                </form>

                {/* OAuth Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-2 bg-white text-gray-500">Or continue with</span>
                  </div>
                </div>

                {/* OAuth Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 border-gray-200 hover:bg-gray-50"
                    onClick={() => handleOAuthLogin('Google')}
                    data-testid="google-oauth-btn"
                  >
                    <Chrome className="w-5 h-5 mr-2 text-blue-500" />
                    Google
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 border-gray-200 hover:bg-gray-50"
                    onClick={() => handleOAuthLogin('Apple')}
                    data-testid="apple-oauth-btn"
                  >
                    <Apple className="w-5 h-5 mr-2" />
                    Apple
                  </Button>
                </div>

                {/* Forgot Password */}
                <div className="mt-4 text-center">
                  <a href="tel:5104224451" className="text-sm text-blue-900 hover:underline">
                    Forgot password? Call (510) 422-4451
                  </a>
                </div>

                {/* Demo Accounts */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <p className="text-xs text-gray-500 text-center mb-3">Quick Login (Demo Accounts)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="text-sm border-gray-200 hover:border-blue-900 hover:text-blue-900"
                      onClick={() => handleDemoLogin('demo1', 'demo1')}
                      disabled={isLocked}
                      data-testid="demo-user-btn"
                    >
                      Demo User
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="text-sm border-gray-200 hover:border-blue-900 hover:text-blue-900"
                      onClick={() => handleDemoLogin('admin1', 'admin1')}
                      disabled={isLocked}
                      data-testid="admin-user-btn"
                    >
                      Admin User
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 text-center mt-3">
                    demo1/demo1 (user) • admin1/admin1 (admin + 2FA)
                  </p>
                </div>
              </>
            )}

            {/* New Client */}
            {!requires2FA && (
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  New to Companeze?{' '}
                  <button 
                    onClick={() => navigate('/interview')}
                    className="text-blue-900 font-medium hover:underline"
                    data-testid="start-interview-link"
                  >
                    Start Interview Lite
                  </button>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-blue-900 text-blue-200 py-6 px-6">
        <div className="max-w-7xl mx-auto text-center text-sm">
          <p>© 2025 Companeze BLDR. All rights reserved.</p>
          <p className="mt-2">
            <a href="tel:5104224451" className="hover:text-white">(510) 422-4451</a>
            {' • '}
            <a href="mailto:info@companeze.com" className="hover:text-white">info@companeze.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Login;
